/**
 * sessionRefresh — Issue #317.
 *
 * Calls `POST /api/v1/auth/refresh` to rotate the access token and stores the
 * new value via `tokenStorage`. The browser sends the `__Host-jzc_rt`
 * HttpOnly cookie automatically; this helper only attaches the
 * `X-Tenant-Id` / `X-Org-Id` headers that the refresh endpoint requires.
 *
 * Single-flight semantics: concurrent callers share one in-flight Promise so a
 * burst of authenticated calls hitting 401 simultaneously cannot stampede
 * the refresh endpoint with N parallel rotations.
 *
 * Idle-expiry contract (Issue #289):
 *   - When `/auth/refresh` itself returns 401 with the backend idle-timeout
 *     body, we surface `status: 'idle_expired'` so the caller can fire
 *     `sessionExpirySignal`. The cookie + storage are then cleared.
 *   - When `/auth/refresh` returns any other failure (network, 5xx, generic
 *     401), we surface `status: 'failed'` and DO NOT clear local state — the
 *     existing 401 response continues to surface to the caller, which the
 *     auth state machine already handles.
 *
 * 红线 (AI GUIDE §6):
 *   - We never log access tokens, refresh tokens, JWT material, cookies,
 *     bearer values, or PII. Failure outcomes carry only a sanitized status
 *     code (`refreshed` / `idle_expired` / `failed`).
 */
import {
  isIdleExpiredResponseBody,
  parseAuthErrorBody,
} from './sessionExpirySignal';
import { storeAccessToken } from './tokenStorage';
import { getStoredSessionIdentity } from './sessionIdentityStorage';

export type RefreshOutcome =
  | { readonly status: 'refreshed'; readonly accessToken: string }
  | { readonly status: 'idle_expired' }
  | { readonly status: 'failed' };

export interface RefreshOptions {
  /** Base URL for /api/v1/auth/refresh. Same value the api-client uses. */
  readonly baseUrl: string;
  /** Optional fetch override (tests + RN environments). */
  readonly fetch?: typeof globalThis.fetch;
}

let inflight: Promise<RefreshOutcome> | null = null;

/**
 * Refresh the access token using the cookie-bound refresh token. Returns a
 * shared in-flight Promise when called concurrently so we never issue
 * multiple rotations for one logical refresh event.
 */
export function refreshAccessToken(opts: RefreshOptions): Promise<RefreshOutcome> {
  if (inflight) return inflight;
  inflight = performRefresh(opts).finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Test-only — drop the in-flight Promise between cases. */
export function __resetInflightRefreshForTests(): void {
  inflight = null;
}

async function performRefresh(opts: RefreshOptions): Promise<RefreshOutcome> {
  const identity = getStoredSessionIdentity();
  if (!identity) {
    return { status: 'failed' };
  }
  const fetchImpl = opts.fetch ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'failed' };
  }
  let response: Response;
  try {
    response = await fetchImpl(`${opts.baseUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Id': identity.tenantId,
        'X-Org-Id': identity.orgId,
      },
      body: '{}',
    });
  } catch {
    // Network error / TypeError. Caller keeps the existing 401 response.
    return { status: 'failed' };
  }

  if (response.ok) {
    let parsed: { accessToken?: unknown } | null = null;
    try {
      const cloned = response.clone();
      parsed = (await cloned.json()) as { accessToken?: unknown };
    } catch {
      return { status: 'failed' };
    }
    if (!parsed || typeof parsed.accessToken !== 'string' || parsed.accessToken.length === 0) {
      return { status: 'failed' };
    }
    storeAccessToken(parsed.accessToken);
    return { status: 'refreshed', accessToken: parsed.accessToken };
  }

  if (response.status === 401) {
    let bodyText: string | null = null;
    try {
      bodyText = await response.clone().text();
    } catch {
      // Swallow read failures; treat as generic failure.
    }
    const body = parseAuthErrorBody(bodyText);
    if (isIdleExpiredResponseBody(body)) {
      return { status: 'idle_expired' };
    }
  }
  return { status: 'failed' };
}
